import { downloadFileFromURL } from "utils/file";

import { Certificate } from "../useUserGuideCertificates";
import { buildCertificateSvg } from "./buildCertificateSvg";
import { svgToDataUrl } from "./svgEncoding";

const PNG_OUTPUT_SIZE = 1200;

const sanitizeFilenamePart = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "certificate";

const loadImage = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load certificate SVG"));
    img.src = src;
  });

const canvasToBlob = (canvas: HTMLCanvasElement) =>
  new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error("Failed to render certificate PNG"));
      }
    }, "image/png");
  });

export const downloadCertificatePng = async (certificate: Certificate): Promise<void> => {
  // Tell the SVG to rasterize at the final output size so the browser renders text and the
  // background's filters at full resolution, instead of decoding at lower size and bitmap-upscaling.
  const svg = await buildCertificateSvg(certificate, PNG_OUTPUT_SIZE);
  const img = await loadImage(svgToDataUrl(svg));

  const canvas = document.createElement("canvas");
  canvas.width = PNG_OUTPUT_SIZE;
  canvas.height = PNG_OUTPUT_SIZE;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Canvas 2D context unavailable");
  }

  ctx.drawImage(img, 0, 0, PNG_OUTPUT_SIZE, PNG_OUTPUT_SIZE);

  const blob = await canvasToBlob(canvas);
  const url = URL.createObjectURL(blob);

  try {
    const filename = `spacelift-${sanitizeFilenamePart(certificate.course)}-certificate.png`;
    downloadFileFromURL(url, filename);
  } finally {
    // The synthetic <a> click starts the download asynchronously; revoking the URL too early
    // can cancel it in some browsers. Defer revocation so the download has time to begin,
    // then release the blob so it can be garbage-collected.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
};
